/**
 * 标书评审工具 — 整合版
 * 
 * 三步骤：
 *   Step 1 — 上传招标文件，7 维度智能分析
 *   Step 2 — 上传 N 份投标文件，逐份评分
 *   Step 3 — 多维度交叉比较 + 风险评估
 *
 * 核心改进：以招标文件为参照，区分"合规相似"与"异常雷同"
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { fmtDateTimeCST } from '../../utils/datetime';
import MarkdownRenderer from '../MarkdownRenderer';
import {
  DocumentArrowUpIcon,
  DocumentPlusIcon,
  XMarkIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ShieldExclamationIcon,
  ChartBarIcon,
  TrashIcon,
  CheckCircleIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { getErrorMessage, API_BASE_URL, submitReview, submitFormalReview, subscribeReviewProgress, fetchReviewResult, pollReviewStatus } from '../../services/api';
import axios from 'axios';

// ---- 类型 ----

interface BiddingAnalysisItem {
  key: string;
  label: string;
  content: string;
}

interface ScoreItem {
  requirement_id: string;
  requirement_title: string;
  score: number;
  max_score: number;
  comment: string;
  deduction_reason: string;
}

interface ScoreCategory {
  category_name: string;
  items: ScoreItem[];
}

interface BidScoreResult {
  name: string;
  total_score: number;
  max_total_score: number;
  categories: ScoreCategory[];
  overall_comment: string;
  bid_text: string;       // 后端提取的投标文件全文，供比较阶段使用
}

interface ScoreMatrix {
  bid_names: string[];
  bid_scores: number[];
  bid_max_scores: number[];
  avg_score: number;
  max_score: number;
  min_score: number;
  score_spread: number;
  radar_labels: string[];
  radar_datasets: RadarDataset[];
}

interface RadarDataset {
  label: string;
  data: number[];
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  pointBackgroundColor: string;
}

interface ComparisonResult {
  expected_alignment_score?: number;
  expected_alignment_detail?: string;
  solution_uniqueness_score?: number;
  solution_uniqueness_detail?: string;
  abnormal_similarity_score?: number;
  abnormal_similarity_detail?: string;
  style_fingerprint_analysis?: string;
  risk_level?: string;
  risk_summary?: string;
  chapter_level_detail?: { chapter: string; alignment_nature: string; description: string }[];
  pairwise_comparisons?: { bid_a: string; bid_b: string; expected_similarity: number; abnormal_similarity: number; key_findings: string }[];
  raw?: string;
  error?: string;
}

interface DisqualificationFinding {
  disqualification_item: string;
  status: 'pass' | 'warning' | 'fail' | 'unverifiable';
  bid_response: string;
  analysis: string;
  recommendation: string;
}

interface DisqualificationBidResult {
  bid_name: string;
  risk_level: 'pass' | 'warning' | 'high_risk';
  summary: string;
  findings: DisqualificationFinding[];
}

interface DisqualificationCheckResult {
  overall_risk_level: string;
  overall_summary: string;
  bid_results: DisqualificationBidResult[];
  raw?: string;
  error?: string;
}

interface LogEntry {
  time: string;
  text: string;
  type: 'info' | 'success' | 'error' | 'phase';
}

interface SavedReview {
  id: number;
  bidding_file_name: string;
  bid_file_names: string[];
  bidding_analysis: BiddingAnalysisItem[];
  bid_scores: BidScoreResult[];
  score_matrix: ScoreMatrix | null;
  comparison: ComparisonResult | null;
  disqualification_check: DisqualificationCheckResult | null;
  risk_report: string;
  risk_level: string;
  total_bids: number;
  review_type?: string;
  elapsed_str: string;
  phase: string;
  created_at: string;
}

// ---- 工具函数 ----

function formatElapsed(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getScorePercent(score: number, max: number): number {
  if (max === 0) return 0;
  return Math.round((score / max) * 100);
}

function getScoreBarColor(percent: number): string {
  if (percent >= 80) return 'bg-green-500';
  if (percent >= 60) return 'bg-yellow-500';
  if (percent >= 40) return 'bg-orange-500';
  return 'bg-red-500';
}

function getRiskColor(level: string): string {
  switch (level) {
    case 'high': return 'text-red-700 bg-red-100 border-red-300';
    case 'medium': return 'text-yellow-700 bg-yellow-100 border-yellow-300';
    case 'low': return 'text-green-700 bg-green-100 border-green-300';
    default: return 'text-gray-600 bg-gray-100 border-gray-300';
  }
}

function getRiskLabel(level: string): string {
  switch (level) {
    case 'high': return '高风险';
    case 'medium': return '中等风险';
    case 'low': return '低风险';
    case 'unknown': return '未评级';
    default: return '';
  }
}

// ---- API ----

async function loadReviewHistory(): Promise<SavedReview[]> {
  try {
    const token = localStorage.getItem('auth_token');
    const res = await axios.get(`${API_BASE_URL}/api/tools/review-history`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.success ? res.data.items : [];
  } catch {
    return [];
  }
}

async function saveReviewHistory(data: {
  bidding_file_name: string;
  bid_file_names: string[];
  bidding_analysis: BiddingAnalysisItem[];
  bid_scores: BidScoreResult[];
  score_matrix: ScoreMatrix | null;
  comparison: ComparisonResult | null;
  disqualification_check: DisqualificationCheckResult | null;
  risk_report: string;
  risk_level: string;
  total_bids: number;
  elapsed_str: string;
  phase?: string;
}): Promise<SavedReview | null> {
  try {
    const token = localStorage.getItem('auth_token');
    const res = await axios.post(`${API_BASE_URL}/api/tools/review-history`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.success ? res.data.item : null;
  } catch {
    return null;
  }
}

async function updateReviewHistory(
  id: number,
  data: Partial<{
    bidding_analysis: BiddingAnalysisItem[];
    bid_scores: BidScoreResult[];
    score_matrix: ScoreMatrix | null;
    comparison: ComparisonResult | null;
    risk_report: string;
    risk_level: string;
    phase: string;
    elapsed_str: string;
  }>,
): Promise<SavedReview | null> {
  try {
    const token = localStorage.getItem('auth_token');
    const res = await axios.put(`${API_BASE_URL}/api/tools/review-history/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.success ? res.data.item : null;
  } catch {
    return null;
  }
}

async function uploadReviewFiles(
  historyId: number,
  biddingFile: File | null,
  bidFiles: File[],
): Promise<{ success: boolean; message?: string }> {
  try {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    if (biddingFile) {
      formData.append('bidding_file', biddingFile);
    }
    bidFiles.forEach((f) => formData.append('bid_files', f));
    const res = await axios.post(
      `${API_BASE_URL}/api/tools/review-history/${historyId}/upload-files`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
        timeout: 120000, // 2 分钟超时
      },
    );
    return { success: res.data.success, message: res.data.message };
  } catch (err: any) {
    const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || '网络错误，请检查服务器连接';
    console.error('[uploadReviewFiles] 上传失败:', msg);
    return { success: false, message: msg };
  }
}

async function downloadReviewFiles(
  historyId: number,
): Promise<{ name: string; stored_name: string; size: number }[]> {
  try {
    const token = localStorage.getItem('auth_token');
    const res = await axios.get(`${API_BASE_URL}/api/tools/review-history/${historyId}/files`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.success ? res.data.files : [];
  } catch {
    return [];
  }
}

// 根据文件扩展名推测 MIME 类型（服务端返回 application/octet-stream 时兼容）
function guessMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();
  const map: Record<string, string> = {
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    doc: 'application/msword',
    md: 'text/markdown',
    markdown: 'text/markdown',
    txt: 'text/plain',
  };
  return map[ext || ''] || 'application/octet-stream';
}

async function downloadReviewFileBlob(
  historyId: number,
  storedName: string,
): Promise<Blob | null> {
  try {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(
      `${API_BASE_URL}/api/tools/review-history/${historyId}/files/${encodeURIComponent(storedName)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

async function deleteReviewHistory(id: number): Promise<boolean> {
  try {
    const token = localStorage.getItem('auth_token');
    const res = await axios.delete(`${API_BASE_URL}/api/tools/review-history/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.success;
  } catch {
    return false;
  }
}

// ---- 组件 ----

export default function BidReview({ refreshToken = 0 }: { refreshToken?: number }) {
  // 文件
  const [biddingFile, setBiddingFile] = useState<File | null>(null);
  const [bidFiles, setBidFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewMode, setReviewMode] = useState<'normal' | 'formal'>('normal');
  const [biddingDragOver, setBiddingDragOver] = useState(false);
  const [bidDragOver, setBidDragOver] = useState(false);

  // 阶段
  type Phase = 'idle' | 'analyzing' | 'scoring' | 'checking' | 'comparing' | 'done';
  const [phase, setPhase] = useState<Phase>('idle');
  const [currentPhaseLabel, setCurrentPhaseLabel] = useState('');

  // Step 1 分析结果
  const [biddingAnalysis, setBiddingAnalysis] = useState<BiddingAnalysisItem[]>([]);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState<string>('');
  const [analysisExpanded, setAnalysisExpanded] = useState<boolean>(true);

  // Step 2 评分结果
  const [bidScores, setBidScores] = useState<BidScoreResult[]>([]);
  const [selectedBidScore, setSelectedBidScore] = useState<number>(0);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Step 3 比较结果
  const [scoreMatrix, setScoreMatrix] = useState<ScoreMatrix | null>(null);
  const [comparison, setComparison] = useState<ComparisonResult | null>(null);
  const [disqualificationCheck, setDisqualificationCheck] = useState<DisqualificationCheckResult | null>(null);
  const [riskReport, setRiskReport] = useState('');
  const [riskLevel, setRiskLevel] = useState('');
  const [activeResultTab, setActiveResultTab] = useState<'matrix' | 'comparison' | 'risk' | 'scores' | 'disqualification'>('matrix');

  // 进度 & 日志
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [savedElapsedStr, setSavedElapsedStr] = useState('');  // 从历史恢复时使用 DB 中保存的耗时字符串
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [showLogs, setShowLogs] = useState(true);  // 完成后仍可查看日志

  // 历史
  const [history, setHistory] = useState<SavedReview[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [exportingReportId, setExportingReportId] = useState<number | null>(null);

  // Refs
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef(0);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const resumeInfoRef = useRef<{ historyId: number; phase: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);  // 用于取消 SSE 订阅
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);  // 空闲检测（长时间无事件触发轮询兜底）
  const isOcrPhaseRef = useRef(false);  // 是否正在 OCR 阶段（空闲超时翻倍）
  // 恢复时缓存从服务端恢复的数据（文件 + 分析 + 评分），避免 setTimeout 的 stale closure 问题
  // handleResumeReview 通过 setTimeout 调用 handleStartReview，
  // 后者若直接读取 state 会捕获到旧 render 的空值
  const resumeCacheRef = useRef<{
    biddingFile: File | null;
    bidFiles: File[];
    biddingAnalysis: BiddingAnalysisItem[];
    bidScores: BidScoreResult[];
  }>({ biddingFile: null, bidFiles: [], biddingAnalysis: [], bidScores: [] });
  const [resumeTarget, setResumeTarget] = useState<SavedReview | null>(null);

  // 计时器
  const startTimer = useCallback(() => {
    elapsedRef.current = 0;
    setElapsedSeconds(0);
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSeconds(elapsedRef.current);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => () => {
    stopTimer();
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); }
  }, [stopTimer]);
  // 首次挂载 + 每次点击左侧"标书评审"（父组件递增 refreshToken）时，重新拉取最新评审历史
  useEffect(() => { loadReviewHistory().then(setHistory); }, [refreshToken]);

  // 日志
  const addLog = useCallback((text: string, type: 'info' | 'success' | 'error' | 'phase' = 'info') => {
    const time = formatElapsed(elapsedRef.current);
    setLogs((prev) => [...prev, { time, text, type }]);
    setTimeout(() => { if (logContainerRef.current) logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight; }, 50);
  }, []);

  // 文件处理
  const handleBiddingFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) { setBiddingFile(file); resetResults(); }
    e.target.value = '';
  };

  const handleBidFilesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    const newFiles = Array.from(selected);
    setBidFiles((prev) => [...prev, ...newFiles]);
    setTimeout(() => { e.target.value = ''; }, 0);
  };

  // 拖拽上传：招标文件（单份，替换已有文件）
  const handleBiddingFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setBiddingDragOver(false);
    if (loading) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setBiddingFile(file);
    resetResults();
  };

  // 拖拽上传：投标文件（多份追加）
  const handleBidFilesDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setBidDragOver(false);
    if (loading) return;
    const newFiles = Array.from(e.dataTransfer.files || []);
    if (newFiles.length === 0) return;
    setBidFiles((prev) => [...prev, ...newFiles]);
  };

  const handleBiddingDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setBiddingDragOver(false);
  };

  const handleBidDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setBidDragOver(false);
  };

  const removeBidFile = (index: number) => {
    setBidFiles((prev) => prev.filter((_, i) => i !== index));
    resetResults();
  };

  const resetResults = () => {
    setError(null);
    setPhase('idle');
    setBiddingAnalysis([]);
    setBidScores([]);
    setScoreMatrix(null);
    setComparison(null);
    setDisqualificationCheck(null);
    setRiskReport('');
    setRiskLevel('');
    setLogs([]);
    setSavedElapsedStr('');
  };

  // ---- 主流程（后台评审 + SSE 实时推送） ----

  const handleStartReview = async () => {
    // 恢复场景：使用 ref 中的文件对象
    const _cache = resumeCacheRef.current;
    const isResumeFromCache = _cache.biddingFile !== null;
    const _bidding = isResumeFromCache ? _cache.biddingFile! : biddingFile;
    const _bids = isResumeFromCache ? _cache.bidFiles : bidFiles;
    const _cachedAnalysis = isResumeFromCache ? _cache.biddingAnalysis : null;
    const _cachedScores = isResumeFromCache ? _cache.bidScores : null;
    resumeCacheRef.current = { biddingFile: null, bidFiles: [], biddingAnalysis: [], bidScores: [] };
    if (!_bidding) { setError('请上传招标文件'); return; }
    if (_bids.length === 0) { setError('请至少上传 1 份投标文件'); return; }

    const resumeInfo = resumeInfoRef.current;
    const isResume = resumeInfo !== null;
    const resumePhase = resumeInfo?.phase || '';

    // 文件丢失后重新上传流程：resumeTarget 已设置但 resumeInfoRef 为空
    const _resumeTarget = resumeTarget;
    setResumeTarget(null);
    const isFileRecovery = !isResume && _resumeTarget !== null;

    setLoading(true);
    setError(null);
    if (!isResume && !isFileRecovery) {
      resetResults();
    }
    startTimer();
    setLogs([]);
    resumeInfoRef.current = null;

    const token = localStorage.getItem('auth_token');
    if (!token) { setError('未登录'); setLoading(false); return; }

    // 已完成的恢复：直接拉取结果
    if (isResume && resumePhase === 'done') {
      await loadCompletedReview(resumeInfo!.historyId, token, _cachedAnalysis, _cachedScores);
      return;
    }

    // 创建 AbortController
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // ----- Step 0: 提交评审任务（后台异步） -----
      let historyId: number;

      if (isFileRecovery) {
        // 文件丢失重新上传：上传到旧 history ID，然后通过 SSE 恢复任务
        historyId = _resumeTarget.id;
        addLog(`【恢复】正在上传文件到任务 (ID: ${historyId})...`, 'phase');
        setCurrentPhaseLabel('正在上传文件...');

        const uploadResult = await uploadReviewFiles(historyId, _bidding, _bids);
        if (!uploadResult.success) {
          throw new Error(uploadResult.message || '文件上传失败');
        }
        addLog('✓ 文件已上传，重新连接评审进度', 'success');

        const recoveryPhase = _resumeTarget.phase || '';
        if (recoveryPhase === 'error') {
          addLog(`【重试】上次评审失败，正在重新执行 (ID: ${historyId})...`, 'phase');
          setCurrentPhaseLabel('正在重新执行评审...');
        } else {
          addLog(`【恢复】重新连接评审进度 (ID: ${historyId})...`, 'phase');
          setCurrentPhaseLabel('正在重新连接评审进度...');
        }
      } else if (isResume && resumeInfo!.historyId) {
        historyId = resumeInfo!.historyId;
        // 恢复已知的分析数据到 state，后续 SSE 会继续
        if (_cachedAnalysis && _cachedAnalysis.length > 0) {
          setBiddingAnalysis(_cachedAnalysis);
          setActiveAnalysisTab(_cachedAnalysis[0].key);
        }
        if (_cachedScores && _cachedScores.length > 0) {
          setBidScores(_cachedScores);
        }
        if (resumePhase === 'error') {
          addLog(`【重试】上次评审失败，正在重新执行 (ID: ${historyId})...`, 'phase');
          setCurrentPhaseLabel('正在重新执行评审...');
        } else if (resumePhase === 'cancelled') {
          addLog(`【恢复】上次评审已取消，正在重新执行 (ID: ${historyId})...`, 'phase');
          setCurrentPhaseLabel('正在重新执行评审...');
        } else {
          addLog(`【恢复】重新连接评审进度 (ID: ${historyId})...`, 'phase');
          setCurrentPhaseLabel('正在重新连接评审进度...');
        }
      } else {
        const isFormal = reviewMode === 'formal';
        addLog(isFormal ? '【正式评审】正在提交扫描件到后台（将启用 OCR 识别）...' : '【提交评审】正在提交文件到后台...', 'phase');
        setCurrentPhaseLabel('正在提交文件...');

        // 提交文件到后台（正式评审走 OCR 端點）
        const submitResult = isFormal
          ? await submitFormalReview(_bidding, _bids, token)
          : await submitReview(_bidding, _bids, token);
        if (!submitResult.success) {
          throw new Error(submitResult.message || '提交评审失败');
        }
        historyId = submitResult.history_id;
        addLog(`✓ ${isFormal ? '正式' : ''}评审任务已提交 (ID: ${historyId})`, 'success');
        addLog(`  后台自动处理，关闭页面不会中断评审`, 'info');
        addLog('', 'info');

        // 前端状态中即时追加此评审记录（后端 review_submit 已写入 DB，
        // 此处不再调用 saveReviewHistory 避免产生重复的历史记录）
        const localRecord: SavedReview = {
          id: historyId,
          bidding_file_name: _bidding.name,
          bid_file_names: _bids.map((f) => f.name),
          bidding_analysis: [],
          bid_scores: [],
          score_matrix: null,
          comparison: null,
          disqualification_check: null,
          risk_report: '',
          risk_level: '',
          total_bids: _bids.length,
          review_type: isFormal ? 'formal' : 'normal',
          elapsed_str: '0 秒',
          phase: 'submitted',
          created_at: new Date().toISOString(),
        };
        setHistory((prev) => [localRecord, ...prev.filter((h) => h.id !== historyId)].slice(0, 20));
      }

      // ----- 订阅 SSE 实时进度 -----
      addLog('【实时进度】等待后台处理...', 'phase');
      setCurrentPhaseLabel('等待后台处理...');

      // 正式评审必然走 OCR，提前延长空闲超时避免误报
      isOcrPhaseRef.current = reviewMode === 'formal';

      let allScores: BidScoreResult[] = [];

      // 空闲检测：OCR 阶段允许更长间隔（大文件单批可能 > 30s），LLM 调用也可能超 30s
      const _getIdleMs = () => (isOcrPhaseRef.current ? 120_000 : 60_000);
      const resetIdleTimer = () => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        const ms = _getIdleMs();
        idleTimerRef.current = setTimeout(async () => {
          addLog(`⏳ 等待后台响应超过 ${ms / 1000} 秒，正在检查任务状态...`, 'info');
          try {
            const status = await pollReviewStatus(historyId, token);
            if (status?.phase === 'done') {
              addLog('✓ 评审已完成（轮询确认）', 'success');
              setPhase('done');
              stopTimer();
              setLoading(false);
              setCurrentPhaseLabel('');
              loadCompletedReview(historyId, token, null, null).catch(() => {});
            } else if (status?.phase === 'error') {
              const errDetail = status?.message || '(无详情，请查看服务端日志)';
              addLog(`✗ 后台任务已失败: ${errDetail}`, 'error');
              setPhase('done');
              stopTimer();
              setLoading(false);
              setCurrentPhaseLabel('');
            } else if (status?.phase) {
              addLog(`【轮询】后台当前阶段: ${status.phase}`, 'info');
              // 继续等待
              resetIdleTimer();
            }
          } catch {
            // 轮询失败，静默忽略
          }
        }, ms);
      };

      // 立即启动一次空闲检测
      resetIdleTimer();

      await subscribeReviewProgress(
        historyId,
        token,
        (event: any) => {
          // 错误事件（兼容两种格式: {error:true,...} 和 {type:"error",...}）
          if (event.error || event.type === 'error') {
            if (event.cancelled) {
              addLog('⏹ 评审已取消', 'error');
            } else {
              addLog(`✗ 错误: ${event.message}`, 'error');
            }
            if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
            return;
          }

          // 心跳 — 重置空闲计时器，证明 SSE 连接存活
          if (event.type === 'heartbeat') {
            resetIdleTimer();
            return;
          }

          // 以下都是有效事件，重置空闲计时器
          resetIdleTimer();

          // 状态快照（重连时恢复）
          if (event.type === 'status_snapshot') {
            if (event.phase === 'submitted') {
              // 仍在初始阶段，正式评审必然在 OCR 中
              isOcrPhaseRef.current = true;
            } else if (event.phase === 'analyzing') setPhase('analyzing');
            else if (event.phase === 'scoring') setPhase('scoring');
            else if (event.phase === 'checking') setPhase('checking');
            else if (event.phase === 'comparing') setPhase('comparing');
            setCurrentPhaseLabel(event.message || '');
            if (event.completed_bids > 0) {
              addLog(`【恢复】后台已完成 ${event.completed_bids}/${event.total_bids} 份评分`, 'info');
            }
          }

          // 阶段变更
          if (event.type === 'phase') {
            if (event.phase === 'ocr') {
              setPhase('analyzing');
              isOcrPhaseRef.current = true;
              addLog('【OCR 识别】正在逐页识别扫描件文字，请耐心等待...', 'phase');
            } else if (event.phase === 'preparing') {
              setPhase('analyzing');
              isOcrPhaseRef.current = false;
            } else if (event.phase === 'analyzing') {
              setPhase('analyzing');
              isOcrPhaseRef.current = false;
              addLog('【步骤 1/3】开始分析招标文件，共 7 个维度', 'phase');
            } else if (event.phase === 'scoring') {
              setPhase('scoring');
              isOcrPhaseRef.current = false;
              addLog('', 'info');
              addLog(event.message || '开始评分', 'phase');
            } else if (event.phase === 'checking') {
              setPhase('checking');
              isOcrPhaseRef.current = false;
              addLog('', 'info');
              addLog(event.message || '开始废标项逐条核查', 'phase');
            } else if (event.phase === 'comparing') {
              setPhase('comparing');
              isOcrPhaseRef.current = false;
              addLog('', 'info');
              addLog(event.message || '开始多维度交叉比较', 'phase');
            }
            setCurrentPhaseLabel(event.message || '');
          }

          // 进度
          if (event.type === 'progress') {
            const msg: string = event.message || '';
            if (msg.startsWith('✓')) {
              addLog(`  ${msg}`, 'success');
            } else if (msg.startsWith('[')) {
              addLog(`  ⏳ ${msg}`, 'info');
              setCurrentPhaseLabel(msg);
            } else if (msg.startsWith('✗') || msg.startsWith('⚠')) {
              addLog(`  ${msg}`, 'error');
            } else {
              addLog(`  ${msg}`, 'info');
              setCurrentPhaseLabel(msg);
            }
          }

          // 单份评分完成
          if (event.type === 'file_result') {
            const result = {
              name: event.name,
              total_score: event.total_score,
              max_total_score: event.max_total_score,
              categories: [],
              overall_comment: '',
              bid_text: '',
            };
            allScores.push(result);
            setBidScores([...allScores]);
            addLog(
              `  ✓ ${event.name} — ${event.total_score}/${event.max_total_score} (${getScorePercent(event.total_score, event.max_total_score)}%)`,
              'success',
            );
            setCurrentPhaseLabel(`正在打分：${event.name} ${allScores.length} 份已完成`);
          }

          // 完成
          if (event.type === 'done') {
            if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
            addLog('', 'info');
            addLog(`✓ 全部评审完成，耗时 ${event.elapsed_seconds || '?'} 秒`, 'success');
            setPhase('done');
            stopTimer();
            setLoading(false);
            setCurrentPhaseLabel('');
            // 更新前端 history 中的评审状态（后端 DB 已在 _update_db_checkpoint 中更新）
            setHistory((prev) =>
              prev.map((h) =>
                h.id === historyId
                  ? { ...h, phase: 'done', elapsed_str: `${event.elapsed_seconds || 0} 秒` }
                  : h,
              ),
            );
            // 拉取完整结果
            loadCompletedReview(historyId, token, null, null).catch(() => {});
          }
        },
        controller.signal,
      );
    } catch (err: any) {
      if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
      if (err?.name === 'AbortError') {
        addLog('⏹ 已取消评审流', 'info');
      } else {
        handleError(err, '评审');
      }
    }
  };

  /** 拉取已完成评审的完整结果 */
  const loadCompletedReview = async (
    historyId: number,
    token: string,
    cachedAnalysis: BiddingAnalysisItem[] | null,
    cachedScores: BidScoreResult[] | null,
  ) => {
    try {
      addLog('正在加载评审结果...', 'info');
      setCurrentPhaseLabel('正在加载评审结果...');

      const result = await fetchReviewResult(historyId, token);

      if (result.bidding_analysis && result.bidding_analysis.length > 0) {
        setBiddingAnalysis(result.bidding_analysis);
        setActiveAnalysisTab(result.bidding_analysis[0].key);
      } else if (cachedAnalysis) {
        setBiddingAnalysis(cachedAnalysis);
        setActiveAnalysisTab(cachedAnalysis[0].key);
      }

      if (result.bid_scores && result.bid_scores.length > 0) {
        setBidScores(result.bid_scores);
      } else if (cachedScores) {
        setBidScores(cachedScores);
      }

      if (result.score_matrix) setScoreMatrix(result.score_matrix);
      if (result.comparison) setComparison(result.comparison);
      if (result.disqualification_check) setDisqualificationCheck(result.disqualification_check);
      if (result.risk_report) setRiskReport(result.risk_report);
      if (result.risk_level) setRiskLevel(result.risk_level);

      setPhase('done');
      stopTimer();
      setLoading(false);
      setCurrentPhaseLabel('');
      addLog('✓ 评审结果加载完成', 'success');
    } catch (err) {
      handleError(err, '加载评审结果');
    }
  };

  /** 取消评审 */
  const handleCancelReview = () => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  const handleError = (err: unknown, stage: string) => {
    const msg = getErrorMessage(err);
    setError(msg);
    addLog(`✗ ${stage}失败：${msg}`, 'error');
    stopTimer();
    setLoading(false);
    setPhase('idle');
    setCurrentPhaseLabel('');
  };

  // ---- 断点续传 ----

  const handleResumeReview = async (saved: SavedReview) => {
    setShowHistory(false);

    // 已完成的任务直接加载结果
    if (saved.phase === 'done') {
      resetResults();
      if (saved.bidding_analysis?.length > 0) {
        setBiddingAnalysis(saved.bidding_analysis);
        setActiveAnalysisTab(saved.bidding_analysis[0].key);
      }
      if (saved.bid_scores?.length > 0) setBidScores(saved.bid_scores);
      if (saved.score_matrix) setScoreMatrix(saved.score_matrix);
      if (saved.comparison) setComparison(saved.comparison);
      if (saved.risk_report) setRiskReport(saved.risk_report);
      if (saved.risk_level) setRiskLevel(saved.risk_level);
      setPhase('done');
      addLog('【恢复】已加载已完成的评审', 'phase');
      return;
    }

    // 进行中的任务：从服务端恢复文件
    setLoading(true);
    setError(null);
    setCurrentPhaseLabel('正在从服务端恢复评审文件...');

    const fileList = await downloadReviewFiles(saved.id);
    if (fileList.length === 0) {
      // 服务端文件丢失，让用户重新上传
      setError(null);
      resetResults();
      setBiddingAnalysis(saved.bidding_analysis || []);
      if (saved.bidding_analysis?.length > 0) {
        setActiveAnalysisTab(saved.bidding_analysis[0].key);
      }
      if (saved.bid_scores?.length > 0) setBidScores(saved.bid_scores);
      setResumeTarget(saved);
      setLoading(false);
      setCurrentPhaseLabel('');
      addLog('⚠ 服务端文件丢失，请重新上传招标文件和投标文件后继续', 'error');
      return;
    }

    // 重建文件对象
    let biddingFileObj: File | null = null;
    const bidFileObjs: File[] = [];

    for (const fileInfo of fileList) {
      const blob = await downloadReviewFileBlob(saved.id, fileInfo.stored_name);
      if (!blob) continue;
      const displayName = fileInfo.stored_name.startsWith('_bidding_')
        ? fileInfo.stored_name.slice('_bidding_'.length)
        : fileInfo.name;
      const f = new File([blob], displayName);
      if (fileInfo.stored_name.startsWith('_bidding_')) {
        biddingFileObj = f;
      } else {
        bidFileObjs.push(f);
      }
    }

    setLoading(false);
    setCurrentPhaseLabel('');

    if (!biddingFileObj && bidFileObjs.length === 0) {
      setError('无法恢复评审文件，请重新上传');
      return;
    }

    // 缓存到 ref，然后触发 handleStartReview
    resumeCacheRef.current = {
      biddingFile: biddingFileObj,
      bidFiles: bidFileObjs,
      biddingAnalysis: saved.bidding_analysis || [],
      bidScores: saved.bid_scores || [],
    };
    resumeInfoRef.current = { historyId: saved.id, phase: saved.phase };

    // 等待 React 下一帧渲染后调用
    setTimeout(() => handleStartReview(), 100);
  };

  // ---- 删除历史 ----

  const handleDeleteReview = async (id: number) => {
    const ok = await deleteReviewHistory(id);
    if (ok) {
      setHistory((prev) => prev.filter((h) => h.id !== id));
      setConfirmDeleteId(null);
    }
  };

  // ---- 还原历史 ----

  const restoreReview = (item: SavedReview) => {
    resetResults();
    setBiddingAnalysis(item.bidding_analysis || []);
    setActiveAnalysisTab(item.bidding_analysis?.[0]?.key || '');
    setBidScores(item.bid_scores || []);
    setScoreMatrix(item.score_matrix || null);
    setComparison(item.comparison || null);
    setDisqualificationCheck(item.disqualification_check || null);
    setRiskReport(item.risk_report || '');
    setRiskLevel(item.risk_level || '');
    setSavedElapsedStr(item.elapsed_str || '');
    if (item.bid_scores?.length > 0) {
      setExpandedCategories(new Set(item.bid_scores[0].categories.map((c: ScoreCategory) => c.category_name)));
    }
    setShowHistory(false);
    setPhase('done');
    // 未完成记录：恢复已有数据，用户可查看已完成的阶段结果
    if (item.phase !== 'done') {
      setCurrentPhaseLabel(item.phase === 'analyzed' ? '分析已完成，评分未完成' : '分析及评分已完成，比较未完成');
    }
  };

  // 导出评审报告（Word docx 下载）
  const handleExportReport = async (item: SavedReview) => {
    if (exportingReportId) return;
    setExportingReportId(item.id);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE_URL}/api/tools/review-history/${item.id}/report`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        let msg = `导出失败（HTTP ${res.status}）`;
        try {
          const data = await res.json();
          if (data?.message) msg = data.message;
        } catch { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      // 优先使用后端 Content-Disposition 提供的文件名
      const cd = res.headers.get('Content-Disposition') || '';
      let filename = `标书评审报告_${item.bidding_file_name}.docx`;
      const m = cd.match(/filename\*=UTF-8''([^;]+)/i);
      if (m && m[1]) filename = decodeURIComponent(m[1]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      addLog(`✓ 评审报告已导出下载：${filename}`, 'success');
    } catch (err) {
      setError(getErrorMessage(err, '评审报告导出失败'));
    } finally {
      setExportingReportId(null);
    }
  };

  const getPhaseLabel = (phase: string): string => {
    switch (phase) {
      case 'analyzed': return '仅分析';
      case 'scored': return '分析+评分';
      case 'done': return '';
      default: return '';
    }
  };

  const getPhaseBadgeColor = (phase: string): string => {
    switch (phase) {
      case 'analyzed': return 'text-orange-700 bg-orange-100 border-orange-300';
      case 'scored': return 'text-blue-700 bg-blue-100 border-blue-300';
      default: return '';
    }
  };

  const toggleCategory = (name: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const hasResults = phase === 'done';
  const currentBidScore = bidScores[selectedBidScore] || null;
  const incompleteReviews = history.filter((h) => h.phase && h.phase !== 'done');

  // ---- 渲染：评分矩阵柱状图 ----

  const ScoreBarChart = ({ matrix }: { matrix: ScoreMatrix }) => {
    const maxPossible = Math.max(...matrix.bid_max_scores, 1);
    return (
      <div className="space-y-3">
        {matrix.bid_names.map((name, idx) => {
          const pct = (matrix.bid_scores[idx] / maxPossible) * 100;
          const barColor = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : pct >= 40 ? 'bg-orange-500' : 'bg-red-500';
          return (
            <div key={name}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-700 truncate max-w-[200px]">{name}</span>
                <span className="text-xs font-mono text-gray-600">{matrix.bid_scores[idx]}/{matrix.bid_max_scores[idx]}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3">
                <div
                  className={`${barColor} rounded-full h-3 transition-all duration-700`}
                  style={{ width: `${Math.min(100, pct)}%` }}
                />
              </div>
            </div>
          );
        })}
        {/* 统计摘要 */}
        <div className="grid grid-cols-4 gap-2 pt-3 border-t border-gray-100">
          <div className="text-center">
            <p className="text-lg font-bold text-gray-800">{matrix.avg_score}</p>
            <p className="text-[10px] text-gray-400">平均分</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-green-600">{matrix.max_score}</p>
            <p className="text-[10px] text-gray-400">最高分</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-red-500">{matrix.min_score}</p>
            <p className="text-[10px] text-gray-400">最低分</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-yellow-600">{matrix.score_spread}</p>
            <p className="text-[10px] text-gray-400">分差</p>
          </div>
        </div>
      </div>
    );
  };

  // ---- 渲染 ----

  return (
    <div className="h-full flex flex-col">

      {/* 标题 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <ShieldExclamationIcon className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">标书评审</h2>
            <p className="text-sm text-gray-500">分析招标文件 → 逐份评分 → 多维交叉比较</p>
          </div>
        </div>
        {history.length > 0 && (
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <ClockIcon className="w-3.5 h-3.5" />
            评审历史 ({history.length})
          </button>
        )}
      </div>

      {/* 未完成评审提示 */}
      {!loading && phase === 'idle' && !resumeTarget && incompleteReviews.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              你有 {incompleteReviews.length} 条未完成的评审
            </span>
          </div>
          <div className="space-y-1.5">
            {incompleteReviews.map((item) => (
              <div key={item.id} className="flex items-center justify-between bg-white border border-amber-200 rounded-md px-3 py-2">
                <div className="min-w-0 flex-1 mr-3">
                  <p className="text-xs font-medium text-gray-800 truncate">
                    {item.bidding_file_name} → {item.bid_file_names.join(' · ')}
                    {item.review_type === 'formal' && (
                      <span className="ml-1 px-1 py-0.5 rounded text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200">
                        正式
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {fmtDateTimeCST(item.created_at)}
                    <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] border font-medium ${getPhaseBadgeColor(item.phase)}`}>
                      {getPhaseLabel(item.phase)}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => handleResumeReview(item)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 transition-colors"
                  >
                    <ArrowPathIcon className="w-3 h-3" />
                    继续评审
                  </button>
                  {confirmDeleteId === item.id ? (
                    <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <span className="text-[11px] text-red-600 font-medium">确定删除？</span>
                      <button onClick={() => handleDeleteReview(item.id)} className="text-[11px] px-1.5 py-0.5 rounded font-medium text-white bg-red-500 hover:bg-red-600">确定</button>
                      <button onClick={() => setConfirmDeleteId(null)} className="text-[11px] px-1.5 py-0.5 rounded font-medium text-gray-500 bg-gray-200 hover:bg-gray-300">取消</button>
                    </span>
                  ) : (
                    <button onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(item.id); }} className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="删除">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 历史记录面板 */}
      {showHistory && (
        <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-700">评审历史</span>
            <button onClick={() => setShowHistory(false)} className="p-0.5 text-gray-400 hover:text-gray-600">
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          </div>
          {history.map((item) => (
            <div
              key={item.id}
              className="w-full text-left px-3 py-2 mb-1 bg-white border border-gray-200 rounded-md hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div
                  className="min-w-0 flex-1 cursor-pointer"
                  onClick={() => restoreReview(item)}
                >
                  <p className="text-xs font-medium text-gray-800 truncate">
                    {item.bidding_file_name} → {item.bid_file_names.join(' · ')}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {fmtDateTimeCST(item.created_at)} · {item.total_bids} 份投标 · {item.elapsed_str}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                  {item.review_type === 'formal' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium text-amber-700 bg-amber-50 border border-amber-200">
                      正式
                    </span>
                  )}
                  {item.phase !== 'done' && item.phase && (
                    <>
                      <span className={`text-[11px] px-1.5 py-0.5 rounded-full border font-medium ${getPhaseBadgeColor(item.phase)}`}>
                        {getPhaseLabel(item.phase)}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleResumeReview(item); }}
                        className="text-[11px] px-2 py-0.5 rounded font-medium text-amber-700 bg-amber-100 border border-amber-300 hover:bg-amber-200 transition-colors"
                      >
                        继续
                      </button>
                    </>
                  )}
                  {item.phase === 'done' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); restoreReview(item); }}
                      className="text-[11px] px-2 py-0.5 rounded font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                    >
                      查看
                    </button>
                  )}
                  {item.phase === 'done' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleExportReport(item); }}
                      disabled={exportingReportId !== null}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="导出 Word 评审报告"
                    >
                      <ArrowDownTrayIcon className="w-3 h-3" />
                      {exportingReportId === item.id ? '导出中…' : '评审报告'}
                    </button>
                  )}
                  {item.risk_level && (
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${getRiskColor(item.risk_level)}`}>
                      {getRiskLabel(item.risk_level)}
                    </span>
                  )}
                  {confirmDeleteId === item.id ? (
                    <span className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <span className="text-[11px] text-red-600 font-medium">确定删除？</span>
                      <button
                        onClick={() => handleDeleteReview(item.id)}
                        className="text-[11px] px-1.5 py-0.5 rounded font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
                      >
                        确定
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-[11px] px-1.5 py-0.5 rounded font-medium text-gray-500 bg-gray-200 hover:bg-gray-300 transition-colors"
                      >
                        取消
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(item.id); }}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                      title="删除"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 断点续传 - 服务端文件丢失，需重新上传 */}
      {resumeTarget && (
        <div className="mb-4 bg-indigo-50 border border-indigo-300 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <ArrowPathIcon className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-medium text-indigo-800">
              继续评审「{resumeTarget.bidding_file_name}」
            </span>
            <span className={`text-[11px] px-1.5 py-0.5 rounded-full border font-medium ${getPhaseBadgeColor(resumeTarget.phase)}`}>
              {getPhaseLabel(resumeTarget.phase)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 mb-2">
            服务端文件已丢失，请重新上传原招标文件和投标文件以继续评审（已保存的分析进度不会丢失）
          </p>
          <button
            onClick={() => { setResumeTarget(null); resetResults(); }}
            className="text-[11px] text-indigo-500 hover:text-red-500 underline"
          >
            取消，改为新建评审
          </button>
        </div>
      )}

      {/* 步骤指示器 */}
      {!loading && phase === 'idle' && (
        <div className="flex items-center gap-0 mb-4">
          {[
            { num: 1, label: '上传招标文件' },
            { num: 2, label: '上传投标文件' },
            { num: 3, label: '多维比较' },
          ].map((step, idx) => (
            <div key={step.num} className="flex items-center gap-0 flex-1">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {step.num}
                </span>
                <span className="text-xs font-medium text-gray-700">{step.label}</span>
              </div>
              {idx < 2 && <ChevronRightIcon className="w-4 h-4 text-gray-300 flex-shrink-0 mx-1" />}
            </div>
          ))}
        </div>
      )}

      {/* 文件上传区域 + 开始按钮 — 仅 idle 阶段显示 */}
      {phase === 'idle' && (
      <>
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* 招标文件 */}
        <div
          onDragOver={(e) => { e.preventDefault(); }}
          onDragEnter={(e) => { e.preventDefault(); if (!loading) setBiddingDragOver(true); }}
          onDragLeave={handleBiddingDragLeave}
          onDrop={handleBiddingFileDrop}
          className={`rounded-lg transition-colors ${biddingDragOver ? 'bg-blue-50/60 ring-2 ring-blue-400' : ''}`}
        >
          <p className="text-sm font-medium text-gray-700 mb-2">
            ① 招标文件 <span className="text-red-500">*</span>
          </p>
          {biddingFile ? (
            <div className="flex items-center justify-between px-3 py-2.5 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 min-w-0">
                <DocumentTextIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <span className="text-sm text-blue-700 truncate">{biddingFile.name}</span>
              </div>
              <button
                onClick={() => { setBiddingFile(null); resetResults(); }}
                disabled={loading}
                className="p-1 text-blue-400 hover:text-red-500 transition-colors disabled:opacity-30"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
              <DocumentArrowUpIcon className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-500 mt-1">点击上传或拖拽文件到此处</span>
              <span className="text-[10px] text-gray-400 mt-0.5">最大 100MB · 支持 PDF / Word / TXT</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.md,.markdown,.txt"
                onChange={handleBiddingFileSelect}
                className="sr-only"
              />
            </label>
          )}
        </div>

        {/* 投标文件 — 逐份添加 */}
        <div
          onDragOver={(e) => { e.preventDefault(); }}
          onDragEnter={(e) => { e.preventDefault(); if (!loading) setBidDragOver(true); }}
          onDragLeave={handleBidDragLeave}
          onDrop={handleBidFilesDrop}
          className={`rounded-lg transition-colors ${bidDragOver ? 'bg-green-50/60 ring-2 ring-green-400' : ''}`}
        >
          <p className="text-sm font-medium text-gray-700 mb-2">
            ② 投标文件 <span className="text-red-500">*</span>
            <span className="text-gray-400 font-normal ml-1">逐份添加，推荐 2-4 份</span>
            {bidFiles.length > 0 && (
              <span className="text-green-600 text-xs ml-1">（已添加 {bidFiles.length} 份）</span>
            )}
          </p>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {/* 已选文件列表 */}
            {bidFiles.map((file, idx) => (
              <div key={`${file.name}-${idx}-${file.size}`} className="flex items-center justify-between px-3 py-2 bg-green-50 rounded border border-green-200">
                <div className="flex items-center gap-2 min-w-0">
                  <DocumentTextIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                  <span className="text-xs text-green-700 truncate">{file.name}</span>
                </div>
                <button
                  onClick={() => removeBidFile(idx)}
                  disabled={loading}
                  className="p-0.5 text-green-400 hover:text-red-500 transition-colors disabled:opacity-30"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          {/* 添加入口 */}
          <label
            className={`relative mt-1.5 flex items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition-colors
              ${bidFiles.length === 0
                ? 'h-24 border-gray-300 hover:border-green-400 hover:bg-green-50/30 flex-col'
                : 'py-2 border-gray-200 hover:border-green-400 hover:bg-green-50/30'
              }`}
          >
            <DocumentPlusIcon className={`${bidFiles.length === 0 ? 'w-6 h-6' : 'w-4 h-4'} text-gray-400 ${bidFiles.length > 0 ? 'mr-1' : ''}`} />
            <span className={`${bidFiles.length === 0 ? 'text-xs mt-1' : 'text-xs'} text-gray-500`}>
              {bidFiles.length === 0 ? '点击添加或拖拽文件到此处' : '继续添加下一份（点击或拖拽）'}
            </span>
            <span className="text-[10px] text-gray-400 ml-1">最大 100MB · 支持 PDF / Word / TXT</span>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.md,.markdown,.txt"
              multiple
              onChange={handleBidFilesSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </label>
        </div>
      </div>

      {/* 评审模式选择 */}
      {!loading && phase === 'idle' && !resumeTarget && (
        <div className="mb-4">
          <div className="flex gap-1.5 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setReviewMode('normal')}
              className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${
                reviewMode === 'normal'
                  ? 'bg-white shadow-sm text-indigo-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <BoltIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
              快速评审
            </button>
            <button
              type="button"
              onClick={() => setReviewMode('formal')}
              className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${
                reviewMode === 'formal'
                  ? 'bg-white shadow-sm text-amber-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <DocumentTextIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
              正式评审
            </button>
          </div>
          {reviewMode === 'formal' && (
            <p className="text-xs text-amber-600 mt-2 px-1">
              适用于盖章签字的扫描件 PDF，将使用 OCR 逐页识别文字内容，处理时间较长（每页约 2-3 秒）。
            </p>
          )}
          {reviewMode === 'normal' && (
            <p className="text-xs text-gray-400 mt-2 px-1">
              适用于 Word 导出的数字化 PDF，直接提取文本快速分析。
            </p>
          )}
        </div>
      )}

      {/* 开始评审按钮 */}
      {!loading && phase === 'idle' && (
        <button
          onClick={handleStartReview}
          disabled={!biddingFile || bidFiles.length === 0}
          className="w-full mb-4 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {resumeTarget ? (
            <>
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              继续评审 — 从{getPhaseLabel(resumeTarget.phase)}阶段恢复 ({bidFiles.length} 份投标文件)
            </>
          ) : (
            <>
              {reviewMode === 'formal' ? (
                <DocumentTextIcon className="w-4 h-4 mr-2" />
              ) : (
                <ShieldExclamationIcon className="w-4 h-4 mr-2" />
              )}
              {reviewMode === 'formal' ? '正式评审' : '开始评审'} ({bidFiles.length} 份投标文件)
            </>
          )}
        </button>
      )}
      </>
      )}

      {/* 进度面板 — 完成后可收起 */}
      {(loading || (logs.length > 0 && showLogs)) && (
        <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              {loading ? (
                <ArrowPathIcon className="w-5 h-5 text-indigo-600 animate-spin flex-shrink-0" />
              ) : (
                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
              )}
              <span className="text-sm font-medium text-gray-800">
                {loading ? currentPhaseLabel || '处理中...' : '评审日志'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {loading && (
                <button
                  type="button"
                  onClick={() => {
                    handleCancelReview();
                    setLoading(false);
                    setPhase('idle');
                    setCurrentPhaseLabel('');
                    stopTimer();
                  }}
                  className="text-xs text-red-500 hover:text-red-700 border border-red-200 hover:border-red-300 rounded px-2 py-0.5 transition-colors cursor-pointer"
                  title="取消评审"
                >
                  取消
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowLogs(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                title="关闭日志"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
            <ClockIcon className="w-3.5 h-3.5" />
            <span className="tabular-nums">已耗时 {formatElapsed(elapsedSeconds)}</span>
          </div>
          {logs.length > 0 && (
            <div ref={logContainerRef}
              className="bg-gray-900 rounded-lg p-3 max-h-48 overflow-y-auto font-mono text-xs leading-relaxed">
              {logs.map((log, i) => (
                <div key={i} className="flex gap-2">
                  {log.text === '' ? <div className="h-3" /> : (
                    <>
                      <span className="text-gray-500 flex-shrink-0 select-none">[{log.time}]</span>
                      <span className={
                        log.type === 'error' ? 'text-red-400'
                          : log.type === 'success' ? 'text-green-400'
                          : log.type === 'phase' ? 'text-yellow-400 font-semibold'
                          : 'text-blue-300'
                      }>{log.text}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* 完成提示 */}
      {hasResults && (
        <div className={`mb-4 p-2.5 rounded-lg flex items-center gap-2 text-xs ${
          riskLevel === 'high' ? 'bg-red-50 border border-red-200 text-red-700'
            : riskLevel === 'medium' ? 'bg-yellow-50 border border-yellow-200 text-yellow-700'
            : 'bg-green-50 border border-green-200 text-green-700'
        }`}>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border flex-shrink-0 ${getRiskColor(riskLevel)}`}>
            {getRiskLabel(riskLevel) || '评审完成'}
          </span>
          <span className="flex-1">总耗时 {savedElapsedStr || formatElapsed(elapsedSeconds)} · {bidFiles.length} 份投标文件</span>
          <button
            onClick={() => { resetResults(); setBiddingFile(null); setBidFiles([]); }}
            className="flex-shrink-0 px-2.5 py-1 text-[11px] font-medium rounded text-indigo-600 bg-white border border-indigo-200 hover:bg-indigo-50 transition-colors"
          >
            新评审
          </button>
        </div>
      )}

      {/* 结果区域 */}
      <div className="flex-1 overflow-y-auto space-y-4">
        {/* ====== 招标文件分析（Tab 展示） ====== */}
        {biddingAnalysis.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MagnifyingGlassIcon className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-800">招标文件分析</h3>
              <span className="text-xs text-gray-400">（{biddingAnalysis.length} 个维度）</span>
              <button
                onClick={() => setAnalysisExpanded(!analysisExpanded)}
                className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-gray-500 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
              >
                {analysisExpanded ? <><ChevronUpIcon className="w-3.5 h-3.5" /> 收起</> : <><ChevronDownIcon className="w-3.5 h-3.5" /> 展开</>}
              </button>
            </div>
            {analysisExpanded && (
              <>
                <div className="flex flex-wrap gap-1 mb-0 border-b border-gray-200">
                  {biddingAnalysis.map((item) => (
                    <button
                      key={item.key}
                      onClick={() => setActiveAnalysisTab(item.key)}
                      className={`px-3 py-2 text-xs font-medium rounded-t-md transition-colors whitespace-nowrap
                        ${activeAnalysisTab === item.key
                          ? 'text-blue-600 bg-white border border-b-white border-gray-200 -mb-px relative z-10'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                <div className="bg-white border-x border-b border-gray-200 rounded-b-lg">
                  {biddingAnalysis.map((item) => (
                    <div key={item.key} className={`px-4 py-3 max-h-80 overflow-y-auto ${activeAnalysisTab === item.key ? 'block' : 'hidden'}`}>
                      {item.content ? <MarkdownRenderer content={item.content} enableMermaid={true} enableMath={true} />
                        : <span className="text-sm text-gray-400">（无内容）</span>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ====== 评分结果 ====== */}
        {bidScores.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <CheckBadgeIcon className="w-4 h-4 text-green-600" />
              投标文件评分
              <span className="text-xs text-gray-400">（共 {bidScores.length} 份）</span>
            </h3>

            {/* 投标文件选择 tabs */}
            <div className="flex flex-wrap gap-1 mb-3 border-b border-gray-200">
              {bidScores.map((s, idx) => (
                <button
                  key={s.name}
                  onClick={() => {
                    setSelectedBidScore(idx);
                    setExpandedCategories(new Set(s.categories.map((c) => c.category_name)));
                  }}
                  className={`px-3 py-2 text-xs font-medium rounded-t-md transition-colors max-w-[180px] truncate
                    ${selectedBidScore === idx
                      ? 'text-green-600 bg-white border border-b-white border-gray-200 -mb-px relative z-10'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  {s.name} <span className="font-mono ml-1">({getScorePercent(s.total_score, s.max_total_score)}%)</span>
                </button>
              ))}
            </div>

            {/* 当前选中投标文件的评分详情 */}
            {currentBidScore && (
              <div className="mb-4">
                {/* 总分卡片 */}
                <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-lg p-4 text-white mb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-100">{currentBidScore.name}</p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-3xl font-bold">{currentBidScore.total_score}</span>
                        <span className="text-green-200">/ {currentBidScore.max_total_score}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-green-100">得分率</p>
                      <p className="text-xl font-bold mt-1">{getScorePercent(currentBidScore.total_score, currentBidScore.max_total_score)}%</p>
                    </div>
                  </div>
                  <div className="mt-3 w-full bg-white/20 rounded-full h-2">
                    <div className="bg-white rounded-full h-2 transition-all duration-500"
                      style={{ width: `${Math.min(100, getScorePercent(currentBidScore.total_score, currentBidScore.max_total_score))}%` }} />
                  </div>
                </div>

                {/* 评分大类 */}
                {currentBidScore.categories.map((cat) => (
                  <div key={cat.category_name} className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-2">
                    <button
                      onClick={() => toggleCategory(cat.category_name)}
                      className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {expandedCategories.has(cat.category_name) ? <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                          : <ChevronRightIcon className="w-4 h-4 text-gray-400" />}
                        <span className="text-sm font-medium text-gray-900">{cat.category_name}</span>
                      </div>
                      <span className="text-sm text-gray-500">
                        {cat.items.reduce((s, i) => s + i.score, 0)}/{cat.items.reduce((s, i) => s + i.max_score, 0)}
                      </span>
                    </button>
                    {expandedCategories.has(cat.category_name) && (
                      <div className="border-t border-gray-100">
                        {cat.items.map((item, idx) => {
                          const pct = getScorePercent(item.score, item.max_score);
                          return (
                            <div key={item.requirement_id || idx} className="px-4 py-2.5 border-b border-gray-50 last:border-b-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm text-gray-800 font-medium">{item.requirement_title}</span>
                                <span className="text-sm font-mono">
                                  <span className={pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-yellow-600' : 'text-red-600'}>
                                    {item.score}
                                  </span>
                                  <span className="text-gray-400"> / {item.max_score}</span>
                                </span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1">
                                <div className={`${getScoreBarColor(pct)} rounded-full h-1.5 transition-all duration-500`}
                                  style={{ width: `${Math.min(100, pct)}%` }} />
                              </div>
                              {item.comment && <p className="text-xs text-gray-600 mt-1">{item.comment}</p>}
                              {item.deduction_reason && <p className="text-xs text-red-500 mt-0.5">扣分原因：{item.deduction_reason}</p>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ====== Step 3: 多维度比较结果 ====== */}
        {(scoreMatrix || comparison || riskReport) && (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <ChartBarIcon className="w-4 h-4 text-indigo-600" />
              多维度交叉比较
              {riskLevel && (
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${getRiskColor(riskLevel)}`}>
                  {getRiskLabel(riskLevel)}
                </span>
              )}
            </h3>

            {/* 结果子 Tab */}
            <div className="flex gap-1 mb-0 border-b border-gray-200">
              {[
                { key: 'matrix', label: '评分矩阵' },
                { key: 'disqualification', label: '废标核查' },
                { key: 'comparison', label: '章节比较' },
                { key: 'risk', label: '风险评估' },
                { key: 'scores', label: '评分对比' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveResultTab(tab.key as typeof activeResultTab)}
                  className={`px-3 py-2 text-xs font-medium rounded-t-md transition-colors
                    ${activeResultTab === tab.key
                      ? 'text-indigo-600 bg-white border border-b-white border-gray-200 -mb-px relative z-10'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="bg-white border-x border-b border-gray-200 rounded-b-lg p-4 max-h-[500px] overflow-y-auto">
              {/* 评分矩阵视图 */}
              {activeResultTab === 'matrix' && scoreMatrix && <ScoreBarChart matrix={scoreMatrix} />}

              {/* 废标核查视图 */}
              {activeResultTab === 'disqualification' && (
                <div className="space-y-4">
                  {!disqualificationCheck ? (
                    <p className="text-sm text-gray-400">（废标核查尚未完成）</p>
                  ) : disqualificationCheck.error ? (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm font-medium text-red-700">废标核查失败</p>
                      <p className="text-xs text-red-600 mt-1">{disqualificationCheck.error}</p>
                    </div>
                  ) : (
                    <>
                      {/* 总体风险等级 */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">总体废标风险：</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${disqualificationCheck.overall_risk_level === 'none' ? 'bg-green-100 text-green-700' : disqualificationCheck.overall_risk_level === 'low' ? 'bg-blue-100 text-blue-700' : disqualificationCheck.overall_risk_level === 'medium' ? 'bg-yellow-100 text-yellow-700' : disqualificationCheck.overall_risk_level === 'high' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                          {disqualificationCheck.overall_risk_level === 'none' ? '无风险' : disqualificationCheck.overall_risk_level === 'low' ? '低风险' : disqualificationCheck.overall_risk_level === 'medium' ? '中风险' : disqualificationCheck.overall_risk_level === 'high' ? '高风险' : '未知'}
                        </span>
                      </div>

                      {/* 总体摘要 */}
                      {disqualificationCheck.overall_summary && (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-sm text-gray-700">{disqualificationCheck.overall_summary}</p>
                        </div>
                      )}

                      {/* 逐份核查结果 */}
                      {disqualificationCheck.bid_results && disqualificationCheck.bid_results.map((bidResult: DisqualificationBidResult, idx: number) => (
                        <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className={`px-4 py-3 flex items-center justify-between ${bidResult.risk_level === 'high_risk' ? 'bg-red-50' : bidResult.risk_level === 'warning' ? 'bg-yellow-50' : 'bg-green-50'}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-800">{bidResult.bid_name}</span>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${bidResult.risk_level === 'high_risk' ? 'bg-red-200 text-red-800' : bidResult.risk_level === 'warning' ? 'bg-yellow-200 text-yellow-800' : 'bg-green-200 text-green-800'}`}>
                                {bidResult.risk_level === 'high_risk' ? '高风险' : bidResult.risk_level === 'warning' ? '需关注' : '通过'}
                              </span>
                            </div>
                          </div>
                          <div className="p-4 space-y-3">
                            {bidResult.summary && (
                              <p className="text-xs text-gray-600">{bidResult.summary}</p>
                            )}
                            {bidResult.findings && bidResult.findings.length > 0 ? (
                              <div className="space-y-2">
                                {bidResult.findings.map((finding: DisqualificationFinding, fIdx: number) => (
                                  <div key={fIdx} className={`p-3 rounded-lg border ${finding.status === 'fail' ? 'bg-red-50 border-red-200' : finding.status === 'warning' ? 'bg-yellow-50 border-yellow-200' : finding.status === 'unverifiable' ? 'bg-gray-50 border-gray-200' : 'bg-green-50 border-green-200'}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${finding.status === 'fail' ? 'bg-red-200 text-red-800' : finding.status === 'warning' ? 'bg-yellow-200 text-yellow-800' : finding.status === 'unverifiable' ? 'bg-gray-200 text-gray-700' : 'bg-green-200 text-green-800'}`}>
                                        {finding.status === 'fail' ? '不满足' : finding.status === 'warning' ? '有风险' : finding.status === 'unverifiable' ? '需核实' : '已满足'}
                                      </span>
                                      <span className="text-xs font-medium text-gray-700">{finding.disqualification_item}</span>
                                    </div>
                                    {finding.bid_response && (
                                      <div className="text-xs text-gray-500 mb-1">
                                        <span className="font-medium text-gray-600">投标响应：</span>
                                        <span className="italic">"{finding.bid_response}"</span>
                                      </div>
                                    )}
                                    {finding.analysis && (
                                      <p className="text-xs text-gray-600 mb-1">{finding.analysis}</p>
                                    )}
                                    {finding.recommendation && (
                                      <p className="text-xs text-indigo-600">
                                        <span className="font-medium">建议：</span>{finding.recommendation}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400">无废标项可供核查</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* 章节级比较视图 */}
              {activeResultTab === 'comparison' && (
                <div className="space-y-4">
                  {(() => {
                    // 判断 comparison 是否有实际可展示的内容
                    const comp = comparison;
                    if (!comp) {
                      return <p className="text-sm text-gray-400">（仅有 1 份投标文件，无需比较）</p>;
                    }
                    const hasError = comp.error && !comp.raw;
                    const hasOnlyRaw = comp.raw && Object.keys(comp).length === 1;
                    const hasContent =
                      comp.expected_alignment_score !== undefined ||
                      comp.solution_uniqueness_score !== undefined ||
                      comp.abnormal_similarity_score !== undefined ||
                      comp.expected_alignment_detail ||
                      comp.solution_uniqueness_detail ||
                      comp.abnormal_similarity_detail ||
                      comp.style_fingerprint_analysis ||
                      (comp.chapter_level_detail && comp.chapter_level_detail.length > 0) ||
                      (comp.pairwise_comparisons && comp.pairwise_comparisons.length > 0);

                    if (hasError) {
                      return (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <p className="text-sm font-medium text-yellow-800 mb-1">比较分析未能完成</p>
                          <p className="text-xs text-yellow-700">{comp.error}</p>
                          <p className="text-xs text-yellow-600 mt-2">
                            可能原因：投标文件过大导致 LLM 超时，或网络波动。建议重新提交评审后重试。
                          </p>
                        </div>
                      );
                    }

                    if (hasOnlyRaw) {
                      return (
                        <div>
                          <p className="text-xs text-yellow-600 mb-2">LLM 返回了非标准格式结果，以下是原始内容：</p>
                          <div className="text-xs text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{comp.raw}</div>
                        </div>
                      );
                    }

                    if (!hasContent) {
                      return (
                        <div className="bg-gray-50 rounded-lg p-4 text-center">
                          <p className="text-sm text-gray-500">（比较数据为空，LLM 可能返回了无法解析的结果）</p>
                        </div>
                      );
                    }

                    return null; // 走下面正常渲染
                  })()}
                  {comparison && !comparison.error && (() => {
                    const comp = comparison;
                    const hasContent =
                      comp.expected_alignment_score !== undefined ||
                      comp.solution_uniqueness_score !== undefined ||
                      comp.abnormal_similarity_score !== undefined ||
                      comp.expected_alignment_detail ||
                      comp.solution_uniqueness_detail ||
                      comp.abnormal_similarity_detail ||
                      comp.style_fingerprint_analysis ||
                      (comp.chapter_level_detail && comp.chapter_level_detail.length > 0) ||
                      (comp.pairwise_comparisons && comp.pairwise_comparisons.length > 0) ||
                      (comp.raw && Object.keys(comp).length === 1);
                    if (!hasContent) return null;
                    return (
                    <>
                      {/* 核心指标卡片 */}
                      <div className="grid grid-cols-3 gap-3">
                        {comparison.expected_alignment_score !== undefined && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-green-600">{comparison.expected_alignment_score}</p>
                            <p className="text-xs text-green-700 mt-1">合规对齐度</p>
                            <p className="text-[10px] text-green-500 mt-0.5">（期望高）</p>
                          </div>
                        )}
                        {comparison.solution_uniqueness_score !== undefined && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-blue-600">{comparison.solution_uniqueness_score}</p>
                            <p className="text-xs text-blue-700 mt-1">方案独特性</p>
                            <p className="text-[10px] text-blue-500 mt-0.5">（期望高）</p>
                          </div>
                        )}
                        {comparison.abnormal_similarity_score !== undefined && (
                          <div className={`${(comparison.abnormal_similarity_score || 0) >= 70 ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'} rounded-lg p-3 text-center`}>
                            <p className={`text-2xl font-bold ${(comparison.abnormal_similarity_score || 0) >= 70 ? 'text-red-600' : 'text-yellow-600'}`}>
                              {comparison.abnormal_similarity_score}
                            </p>
                            <p className="text-xs text-gray-700 mt-1">异常雷同度</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">（期望低）</p>
                          </div>
                        )}
                      </div>

                      {/* 详细信息 */}
                      {comparison.expected_alignment_detail && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-green-800 mb-1">合规相似说明</h4>
                          <p className="text-xs text-gray-700 whitespace-pre-wrap">{comparison.expected_alignment_detail}</p>
                        </div>
                      )}
                      {comparison.solution_uniqueness_detail && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-blue-800 mb-1">方案差异分析</h4>
                          <p className="text-xs text-gray-700 whitespace-pre-wrap">{comparison.solution_uniqueness_detail}</p>
                        </div>
                      )}
                      {comparison.abnormal_similarity_detail && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-red-800 mb-1">异常雷同详情</h4>
                          <p className="text-xs text-red-700 whitespace-pre-wrap">{comparison.abnormal_similarity_detail}</p>
                        </div>
                      )}
                      {comparison.style_fingerprint_analysis && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-gray-800 mb-1">风格指纹分析</h4>
                          <p className="text-xs text-gray-700 whitespace-pre-wrap">{comparison.style_fingerprint_analysis}</p>
                        </div>
                      )}

                      {/* 章节级详情 */}
                      {comparison.chapter_level_detail && comparison.chapter_level_detail.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-800 mb-2">章节级对齐分析</h4>
                          <div className="space-y-1.5">
                            {comparison.chapter_level_detail.map((ch, idx) => (
                              <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded text-xs">
                                <span className={
                                  ch.alignment_nature === 'expected' ? 'text-green-600' :
                                  ch.alignment_nature === 'abnormal' ? 'text-red-600' :
                                  'text-yellow-600'
                                }>
                                  {ch.alignment_nature === 'expected' ? '✓' : ch.alignment_nature === 'abnormal' ? '⚠' : '~'}
                                </span>
                                <div>
                                  <span className="font-medium text-gray-800">{ch.chapter}</span>
                                  <span className="text-gray-600 ml-1">— {ch.description}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 两两比较 */}
                      {comparison.pairwise_comparisons && comparison.pairwise_comparisons.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-800 mb-2">两两比较详情</h4>
                          <div className="space-y-2">
                            {comparison.pairwise_comparisons.map((pair, idx) => (
                              <div key={idx} className="bg-gray-50 rounded-lg p-3 text-xs">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-gray-800">{pair.bid_a} vs {pair.bid_b}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-green-600">合规 {pair.expected_similarity}%</span>
                                    <span className={pair.abnormal_similarity >= 60 ? 'text-red-600 font-bold' : 'text-yellow-600'}>
                                      异常 {pair.abnormal_similarity}%
                                    </span>
                                  </div>
                                </div>
                                <p className="text-gray-600 whitespace-pre-wrap">{pair.key_findings}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* LLM 返回的原始文本（raw） */}
                      {comparison.raw && Object.keys(comparison).length === 1 && (
                        <div className="text-xs text-gray-600 whitespace-pre-wrap">{comparison.raw}</div>
                      )}
                    </>
                  );
                })()}
              </div>
              )}

              {/* 风险评估视图 */}
              {activeResultTab === 'risk' && (
                <div>
                  {riskReport ? (
                    <div className="text-sm">
                      <MarkdownRenderer content={riskReport} enableMermaid={true} enableMath={true} />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">（无风险评估报告）</p>
                  )}
                </div>
              )}

              {/* 评分对比视图 */}
              {activeResultTab === 'scores' && (
                <div>
                  {bidScores.length > 1 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-2 font-medium text-gray-600">评分项</th>
                            {bidScores.map((s) => (
                              <th key={s.name} className="text-center py-2 px-2 font-medium text-gray-600 max-w-[120px] truncate">
                                {s.name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-100 bg-gray-50">
                            <td className="py-2 px-2 font-semibold text-gray-800">总分</td>
                            {bidScores.map((s) => {
                              const pct = getScorePercent(s.total_score, s.max_total_score);
                              return (
                                <td key={s.name} className={`text-center py-2 px-2 font-mono font-semibold ${pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                                  {s.total_score}/{s.max_total_score}
                                </td>
                              );
                            })}
                          </tr>
                          {bidScores[0]?.categories.flatMap((cat) =>
                            cat.items.map((item) => (
                              <tr key={`${cat.category_name}-${item.requirement_id}`} className="border-b border-gray-50">
                                <td className="py-1.5 px-2 text-gray-700">{cat.category_name} · {item.requirement_title}</td>
                                {bidScores.map((s) => {
                                  const foundCat = s.categories.find((c) => c.category_name === cat.category_name);
                                  const foundItem = foundCat?.items.find((i) => i.requirement_id === item.requirement_id);
                                  const score = foundItem?.score ?? 0;
                                  const max = foundItem?.max_score ?? 0;
                                  return (
                                    <td key={s.name} className="text-center py-1.5 px-2 font-mono text-gray-600">
                                      {score}/{max}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">（需要至少 2 份投标文件才能对比评分）</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
